import { Component } from '@angular/core';
import { first, tap } from 'rxjs';
import { indicate } from 'src/app/operators';
import {
  DictionaryItem,
  DictionaryItemDefinition,
  DictionarySimplePageComponent
} from 'src/app/shared/components/dictionary-simple-page/dictionary-simple-page.component';
import { BranchTableCodeViewModel, EsraPipingAPIClient } from 'src/app/shared/models/autogenerated-piping';
import { DictionaryDeleteComponent } from '../dialogs/dictionary-delete/dictionary-delete.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { InsightsService } from 'src/app/shared/services/insights.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DictionaryExportService } from '../services/dictionary-export.service';
import { BranchTableCodeAddEditComponent } from '../dialogs/branch-table-code-add-edit/branch-table-code-add-edit.component';

@Component({
    selector: 'app-branch-table-codes',
    templateUrl: '../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.html',
    styleUrls: ['../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.scss'],
    standalone: false
})
export class BranchTableCodesComponent extends DictionarySimplePageComponent<BranchTableCodeViewModel> {
  constructor(
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly insightsService: InsightsService,
    private readonly dialog: MatDialog,
    private readonly dictionaryExportService: DictionaryExportService
  ) {
    super();
    this.showClearSearchButton = false;
    this.exportFn = () => this.dictionaryExportService.exportBranchTableCodes();
  }

  protected childOnInitBefore(): void { /* do nothing */ }
  protected childOnInitAfter(): void { /* do nothing */ }

  protected searchAll(): void {
    this.esraApiClient.getAllCurrentBranchTableCodes()
      .pipe(
        indicate(this.searching$),
        first(),
        tap(branchTableCodes => {
          this.dataSource.data = branchTableCodes;
          this.totalCount = branchTableCodes.length;
        }))
        .subscribe();
  }

  protected initializeDictionaryDefinition(): DictionaryItemDefinition<BranchTableCodeViewModel>{
    return {
      displayStatusColumn: true,
      dictionaryItems: [
          new DictionaryItem('Code','code'),
          new DictionaryItem('Description', 'description', 'extended')
        ]
    };
  }

  protected getFilterPredicate(record: BranchTableCodeViewModel, parametersMap: Map<string, string>): boolean {
    const searchQuery = parametersMap.get(DictionarySimplePageComponent.SEARCH_QUERY_NAME)?.toLowerCase() || '';
    const isActiveFilter = parametersMap.get(DictionarySimplePageComponent.IS_ACTIVE_NAME);

    const matchesSearchQuery = (record.code.toLowerCase().includes(searchQuery) ||
        record.description.toLowerCase().includes(searchQuery) );

    if (isActiveFilter == null) {
        return matchesSearchQuery;
    } else {
        return matchesSearchQuery && record.isActive === JSON.parse(isActiveFilter);
    }
  }

  protected createNew(): void {
    this.openEditor(null);
  }

  protected editItem(branchTableCode: BranchTableCodeViewModel): void {
    this.openEditor(branchTableCode);
  }

  private openEditor(branchTableCode: BranchTableCodeViewModel | null): void {
    const dialogRef: MatDialogRef<BranchTableCodeAddEditComponent, boolean> = this.dialog.open<
      BranchTableCodeAddEditComponent,
      BranchTableCodeViewModel | null,
      boolean
    >(BranchTableCodeAddEditComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: true,
      data: branchTableCode
    });

    dialogRef.afterClosed().pipe(first()).subscribe((saved: boolean | undefined) => {
      if (saved) {
        this.searchAll();
      }
    });
  }

  protected editItemReferenced(item: object): void {
    throw new Error('Method not implemented.');
  }

  protected deleteItem(item: BranchTableCodeViewModel): void {
    this.dialog.open(DictionaryDeleteComponent, {
      width: '40%',
      height: '23%',
      data: {item: item,
        objectName: item.code,
        objectType: 'branch table code'
      }
    }).afterClosed().subscribe((result) => {
      if(result) {
        this.esraApiClient.archiveBranchTableCode(item.id)
        .pipe(first(),
          tap(item => {
              this.snackBarService.showInfo("Branch Table Code: " + item.code + " was archived");
              this.searchAll();
            }))
        .subscribe({
          error: (err) => {
            this.snackBarService.showInfo("Unable to archive Branch Table Code: " + item.code + "");
            this.insightsService.logException(err);
          },
        });
      }
    })
  }

  protected updateStatus(item: BranchTableCodeViewModel) {
    this.esraApiClient.toggleIsActiveBranchTableCode(item.isActive, [item.id]).pipe(indicate(this.searching$), first(),
    tap(() => this.search())).subscribe();
  }

  protected getUsageInformation(item: object): void {
    throw new Error('Method not implemented.');
  }

  protected getNoMatchMessage(): string {
    return "No branch table codes match provided search criteria";
  }
}
